import React, { useEffect, useId, useRef, useState } from 'react';
import {
  CheckBoxParam,
  Column,
  Fieldset,
  Form,
  Input,
  Option,
  RequiredHelpPhrase,
  Row,
  Select,
  SelectParam,
  SonForm,
  SubmitButton,
  Switch,
  TextParam,
  useForm,
} from '../../index';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { AxiosResponse } from 'axios';
import { ConditionValues } from '../../../../types/api/AttributeGroups';
import { PseudoInput } from '../../elements/PseudoInput/PseudoInput';
import { HyperlinkTarget } from '../../../../components/atoms/Hyperlink';
import { AreaPageTypeSelect, AreaPageTypeValue } from './partials/AreaPageTypeSelect';
import { sortSelectOptions } from './partials/CreatEditPageMembershipMatchingRuleFormUtils';
import { ContentLink } from '../../../components/ContentLink';
import { TooltipPlacement } from '../../../../components/atoms/Tooltip';
import { Form2Modifier } from '../../Form';
import { RowModifier } from '../../grid/Row/Row';
import { FieldsetModifier } from '../../elements/Fieldset/Fieldset';
import { ColumnModifier } from '../../grid/Column/Column';
import { SwitchModifier } from '../../elements/Switch/Switch';

type areaMatchingAttributeValue =
  | 'honorificPrefix'
  | 'academicTitle'
  | 'firstName'
  | 'lastName'
  | 'conferencePhone'
  | 'additionalPhoneNumber'
  | 'conferenceId'
  | 'mobileNumber'
  | 'faxNumber'
  | 'location'
  | 'personCategory'
  | 'room'
  | 'emailAddress'
  | 'institution'
  | 'institutionGroup'
  | 'officeDenotation'
  | 'orgUnit'
  | 'orgUnitDesignation'
  | 'subjectArea'
  | 'phoneNumber'
  | 'availabilityTimes'
  | 'conferenceUrl'
  | 'externalGroups'
  | 'personExtendedData01'
  | 'personExtendedData02'
  | 'personExtendedData03'
  | 'personExtendedData04'
  | 'personExtendedData05'
  | 'additionalEmailAddress01'
  | 'additionalEmailAddress02'
  | 'additionalEmailAddress03'
  | 'additionalEmailAddress04'
  | 'additionalEmailAddress05'
  | 'fibAccessLevel';

export type CreateEditPageMembershipMatchingRuleForm = {
  form: SonForm<
    | SelectParam<'areaPageType', { value: AreaPageTypeValue }>
    | SelectParam<'areaMatchingAttribute', { value: areaMatchingAttributeValue }>
    | SelectParam<'roleId'>
    | CheckBoxParam<'hasRoleCondition'>
    | SelectParam<'roleMatchingAttribute'>
    | SelectParam<'roleMatchingCondition', { value: ConditionValues }>
    | TextParam<'roleMatchingValue'>
  >;
  onSuccess?: (response: AxiosResponse) => void;
  pageToRole: Record<string, string[]>;
  areaSiteMapUrl: string;
};

export function CreateEditPageMembershipMatchingRuleForm({
  form,
  onSuccess,
  pageToRole,
  areaSiteMapUrl,
}: CreateEditPageMembershipMatchingRuleForm) {
  const { t } = useSonTranslation('PageMembershipMatchingRuleSettingsForm');
  const roleMatchingValueRef = useRef<HTMLInputElement | null>(null);

  const { formProps, params, fetchState } = useForm({ form, onSuccess: onSuccess });

  const areaPageTypeParam = params.getByName('areaPageType');
  const areaMatchingAttributeParam = params.getByName('areaMatchingAttribute');
  const roleIdParam = params.getByName('roleId');
  const hasRoleConditionParam = params.getByName('hasRoleCondition');
  const roleMatchingAttributeParam = params.getByName('roleMatchingAttribute');
  const roleMatchingConditionParam = params.getByName('roleMatchingCondition');
  const roleMatchingValueParam = params.getByName('roleMatchingValue');

  const [selectedAreaPageType, setSelectedAreaPageType] = useState<string | undefined>(areaPageTypeParam.value);
  const [selectedRoleMatchingCondition, setSelectedRoleMatchingCondition] = useState<string | undefined>(
    roleMatchingConditionParam.value
  );

  const [addCondition, setAddCondition] = useState(hasRoleConditionParam.checked);

  useEffect(() => {
    if (selectedRoleMatchingCondition === 'isEmpty' && roleMatchingValueRef.current) {
      roleMatchingValueRef.current.value = '';
    }
  }, [selectedRoleMatchingCondition]);

  const formId = useId();

  return (
    <Form
      id={`page-membership-matching-rule-settings-form-${formId}`}
      modifier={Form2Modifier.NO_PADDING_BOTTOM}
      {...formProps}
    >
      <RequiredHelpPhrase />
      <Row modifier={RowModifier.GRID_3_COLUMNS_2FR_1FR_3FR}>
        <Column modifier={ColumnModifier.SPAN_1_AND_2}>
          <AreaPageTypeSelect
            areaPageTypeParam={areaPageTypeParam}
            onChange={event => setSelectedAreaPageType(event.target.value)}
          />
        </Column>
        {selectedAreaPageType ? (
          <Column>
            <div className="form2--margin-top">
              <ContentLink
                to={`${areaSiteMapUrl}&pageType=${selectedAreaPageType}`}
                title={t('phrases:linkOpensInNewWindow')}
                target={HyperlinkTarget.BLANK}
                tooltipPlacement={TooltipPlacement.TOP}
                routing={false}
                external
                newWindowIndicator
              >
                {t('inputs.areaPageTypeLink')}
              </ContentLink>
            </div>
          </Column>
        ) : null}
      </Row>
      <Fieldset legend={t('inputs.areaMatchingFieldSetLegend')} modifier={FieldsetModifier.NO_MARGIN}>
        <Row modifier={RowModifier.GRID_3_COLUMNS_2FR_1FR_3FR}>
          <Column modifier={ColumnModifier.SPAN_1_AND_2}>
            <Select
              label={t('inputs.areaMatchingAttribute.label')}
              param={areaMatchingAttributeParam}
              defaultValue={areaMatchingAttributeParam.value}
            >
              {sortSelectOptions(
                areaMatchingAttributeParam.options,
                key => t(`PersonAttributes:${key}` as any),
                true
              ).map(option => (
                <Option key={option.value} value={option.value}>
                  {t(`PersonAttributes:${option.value}` as any)}
                </Option>
              ))}
            </Select>
          </Column>
          <Column>
            <PseudoInput label={t('inputs.areaMatchingValue.label')} value={t('inputs.areaMatchingValue.value')} />
          </Column>
        </Row>
      </Fieldset>
      <>
        <hr className="page-membership-matching-rule-list__hr" />
        <Row modifier={[RowModifier.ALIGN_ITEMS_FLEX_END, RowModifier.GRID_3_COLUMNS_2FR_1FR_3FR]}>
          <Column modifier={ColumnModifier.SPAN_1_AND_2}>
            <Select label={t('inputs.roleName.label')} param={roleIdParam} defaultValue={roleIdParam.value}>
              {roleIdParam.options
                .filter(option =>
                  selectedAreaPageType && pageToRole ? pageToRole[selectedAreaPageType]?.includes(option.value) : true
                )
                .slice()
                .sort((a: any, b: any) => a.text.localeCompare(b.text))
                .map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.text}
                  </Option>
                ))}
            </Select>
          </Column>
          <Column>
            <Switch
              label={t(`inputs.${hasRoleConditionParam.name}.label`)}
              param={hasRoleConditionParam}
              modifierLabel={SwitchModifier.POSITION_INHERIT}
              onChange={event => setAddCondition(event.target.checked)}
            />
          </Column>
        </Row>
        {addCondition ? (
          <Fieldset legend={t('inputs.roleMatchingLegend')}>
            <Row modifier={RowModifier.GRID_3_COLUMNS_2FR_1FR_3FR}>
              <Column>
                <Select
                  label={t('inputs.roleMatchingAttribute.label')}
                  param={roleMatchingAttributeParam}
                  defaultValue={roleMatchingAttributeParam.value}
                >
                  {sortSelectOptions(
                    roleMatchingAttributeParam.options,
                    key => t(`PersonAttributes:${key}` as any),
                    true
                  )
                    .filter((option): option is { value: string } => !!option.value)
                    .map(option => (
                      <Option key={option.value} value={option.value}>
                        {t(`PersonAttributes:${option.value}` as any)}
                      </Option>
                    ))}
                </Select>
              </Column>
              <Column>
                <Select
                  label={t('inputs.roleMatchingCondition.label')}
                  param={roleMatchingConditionParam}
                  required
                  onChange={event => setSelectedRoleMatchingCondition(event.target.value)}
                >
                  {sortSelectOptions(roleMatchingConditionParam.options, key =>
                    t(`phrases:ruleSetConditions.${key}` as any)
                  ).map(option => (
                    <Option
                      key={option.value}
                      value={option.value}
                      selected={option.value === roleMatchingConditionParam.value}
                    >
                      {t(`phrases:ruleSetConditions.${option.value}` as any)}
                    </Option>
                  ))}
                </Select>
              </Column>
              <Column>
                <Input
                  ref={roleMatchingValueRef}
                  param={roleMatchingValueParam}
                  label={t('inputs.roleMatchingValue.label')}
                  readOnly={selectedRoleMatchingCondition === 'isEmpty'}
                  required={selectedRoleMatchingCondition !== 'isEmpty'}
                />
              </Column>
            </Row>
          </Fieldset>
        ) : null}
      </>
      <Row>
        <SubmitButton
          buttonLabel={t('inputs.submitButtonLabel')}
          messageSuccess={t('status.success')}
          messageSending={t('status.sending')}
          messageFail={t('status.fail')}
          messageForward={t('status.forwardSuccess')}
          fetchState={fetchState}
        />
      </Row>
    </Form>
  );
}
